import * as React from 'react';
import { NavLink, useLocation } from 'react-router';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';
import { navigationPatterns } from '../design-system/patterns/navigation';
import { focusUtils } from '../design-system/utils/a11y';
import { UniversalMobileNav } from './UniversalMobileNav';

export interface TopNavItem {
  label: string;
  to: string;
  /** Lucide icon — render as `<Icon className="h-4 w-4" />`. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Use `end` matching for index routes so siblings don't false-match. */
  end?: boolean;
  /** Optional badge (e.g. unread tickets). */
  badge?: string | number;
}

export interface UniversalTopNavProps {
  /** Brand mark — typically logo + app label (e.g. "Afrilink · Client"). */
  brand: React.ReactNode;
  /** Primary nav items rendered between brand and right cluster. */
  items: TopNavItem[];
  /** Right cluster — notifications, app switcher, user menu. */
  right?: React.ReactNode;
  /** Override the default mobile sheet. Omit it — `UniversalMobileNav` is
   *  rendered automatically from `items` below `md`. Only pass this when an app
   *  genuinely needs different mobile navigation from its desktop nav. */
  mobileMenu?: React.ReactNode;
  /** App theme accent — rendered as a 2px bottom border for app identity. */
  accentColor?: string;
  /** Hero image URL used as a darkened background. Defaults to the corporate
   *  hero shipped under `/images/hero-corporate-main.png`. Pass `null` to
   *  disable and fall back to a flat dark fill. */
  backgroundImage?: string | null;
  className?: string;
}

/**
 * UniversalTopNav — primary navigation for every authenticated app.
 *
 * Architecture decision (2026-04-29): the portal uses top-bar-only navigation.
 * There is no sidebar. Primary nav lives here; in-section navigation uses
 * `UniversalSubNav` or route-aware tabs.
 */
const DEFAULT_HERO = '/images/hero-corporate-main.png';

export function UniversalTopNav({
  brand,
  items,
  right,
  mobileMenu,
  accentColor,
  backgroundImage = DEFAULT_HERO,
  className,
}: UniversalTopNavProps) {
  return (
    <header
      className={cn(navigationPatterns.topBar.wrapper, 'bg-neutral-900', className)}
      style={
        accentColor
          ? { borderBottomColor: accentColor, borderBottomWidth: '2px' }
          : undefined
      }
    >
      {/* Layer 1 — hero image (cover, centred). */}
      {backgroundImage && (
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${backgroundImage}')` }}
        />
      )}
      {/* Layer 2 — dark overlay so white text stays legible. */}
      {backgroundImage && (
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/65 to-black/55"
        />
      )}

      <div className={navigationPatterns.topBar.brand}>
        {/* Phase 4 / ADR R2: this used to be `{mobileMenu && …}` — an optional
            slot that ALL THREE apps forgot to pass, so the portal shipped with
            no mobile navigation whatsoever. A default is rendered now, so a
            consumer has to opt OUT rather than remember to opt in. */}
        {items.length > 0 && (
          <div className="md:hidden">
            {mobileMenu ?? <UniversalMobileNav items={items} />}
          </div>
        )}
        {brand}
      </div>

      <PrimaryNav items={items} />

      <div className={navigationPatterns.topBar.rightCluster}>{right}</div>
    </header>
  );
}

/** Width reserved for the "More" trigger when it is showing. */
const MORE_TRIGGER_PX = 96;
/** `gap-1` between items. */
const ITEM_GAP_PX = 4;

/**
 * PrimaryNav — priority-plus overflow.
 *
 * The console has 11 sections. Eleven LABELLED items need ~1,640px of bar once
 * the brand and user menu are accounted for, and labels return at `lg`
 * (1,024px) — so every 14" laptop (1366 / 1440 / 1512) sat in a ~600px dead
 * zone where the bar silently horizontal-scrolled and the last sections were
 * simply off-screen. Reported by Mr Mampe, 2026-08-30.
 *
 * Items keep their declared order and are never re-ranked by usage: the same
 * viewport width always yields the same nav, so muscle memory survives. Only
 * the tail moves, and only when it genuinely does not fit.
 *
 * This is NOT a third navigation level (CLAUDE.md §4.1). Every item is still a
 * level-one destination; "More" is an overflow affordance for a bar that ran
 * out of pixels, which is why it holds no groups and no nesting.
 *
 * MEASUREMENT: a hidden mirror of the full list is rendered and measured. The
 * obvious approach — measuring the visible items — cannot grow the bar back,
 * because a hidden item reports offsetWidth 0 and would never be measured
 * wide enough to return. The mirror always reports true widths at the current
 * breakpoint, so the nav recovers correctly when the window is widened.
 */
function PrimaryNav({ items }: { items: TopNavItem[] }) {
  const { pathname } = useLocation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mirrorRef = React.useRef<HTMLUListElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(items.length);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const mirror = mirrorRef.current;
    if (!container || !mirror) return;

    const measure = () => {
      const widths = Array.from(mirror.children).map(
        (c) => (c as HTMLElement).offsetWidth + ITEM_GAP_PX,
      );
      const total = widths.reduce((a, b) => a + b, 0);
      const available = container.clientWidth;

      if (total <= available) {
        setVisibleCount(items.length);
        return;
      }
      // Everything shown alongside "More" has to leave room for it.
      let used = MORE_TRIGGER_PX;
      let n = 0;
      for (const w of widths) {
        if (used + w > available) break;
        used += w;
        n += 1;
      }
      setVisibleCount(n);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [items]);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  // If the page you are on lives under "More", the trigger has to say so —
  // otherwise the bar shows no active section at all and reads as "nowhere".
  const overflowHasActive = overflow.some(
    (i) => pathname === i.to || (!i.end && pathname.startsWith(i.to + '/')),
  );

  return (
    <nav
      className={navigationPatterns.topBar.primaryCluster}
      aria-label="Primary"
      ref={containerRef as unknown as React.Ref<HTMLElement>}
    >
      {/* Measurement mirror: same markup, same breakpoint classes, never shown
          and never announced.
          The 0x0 `overflow-hidden` wrapper is load-bearing. The container is
          `overflow-x-auto`, and an absolutely-positioned child WIDER than it
          still extends scrollWidth — which would leave a phantom scroll region
          the width of the full item list, the exact symptom being fixed.
          Clipping it to nothing keeps scrollWidth honest while the children
          still lay out and report real offsetWidth. `w-max` stops the flex row
          collapsing inside a zero-width parent. */}
      <div aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-0 w-0 overflow-hidden">
        <ul ref={mirrorRef} className="flex w-max items-center gap-1">
          {items.map((item) => (
            <li key={item.to}>
              <TopNavLink item={item} />
            </li>
          ))}
        </ul>
      </div>

      <ul className="flex items-center gap-1">
        {visible.map((item) => (
          <li key={item.to}>
            <TopNavLink item={item} />
          </li>
        ))}
        {overflow.length > 0 && (
          <li>
            <DropdownPrimitive.Root>
              <DropdownPrimitive.Trigger
                className={cn(
                  overflowHasActive
                    ? navigationPatterns.topBar.linkActive
                    : navigationPatterns.topBar.link,
                  focusUtils.ring,
                )}
                aria-label={`More sections (${overflow.length})`}
              >
                More
                <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
              </DropdownPrimitive.Trigger>
              <DropdownPrimitive.Portal>
                <DropdownPrimitive.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 min-w-48 rounded-md border border-border bg-card p-1 shadow-lg"
                >
                  {overflow.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownPrimitive.Item key={item.to} asChild>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={({ isActive }) =>
                            cn(
                              'flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none',
                              isActive
                                ? 'bg-primary/10 font-medium text-primary'
                                : 'text-foreground hover:bg-muted',
                            )
                          }
                        >
                          {Icon && <Icon className="h-4 w-4 shrink-0" />}
                          {item.label}
                        </NavLink>
                      </DropdownPrimitive.Item>
                    );
                  })}
                </DropdownPrimitive.Content>
              </DropdownPrimitive.Portal>
            </DropdownPrimitive.Root>
          </li>
        )}
      </ul>
    </nav>
  );
}

function TopNavLink({ item }: { item: TopNavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      // The label is hidden between md and lg (icon-only), so the accessible
      // name has to come from aria-label — otherwise the link is unnamed for
      // screen readers at those widths. `title` gives sighted users a tooltip.
      aria-label={item.label}
      title={item.label}
      className={({ isActive }) =>
        cn(
          isActive ? navigationPatterns.topBar.linkActive : navigationPatterns.topBar.link,
          focusUtils.ring
        )
      }
    >
      {({ isActive }) => (
        <>
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          {/* Icon-only at md–lg: 8–9 labelled items cannot fit a 768–1024px
              bar. Labels return at lg. Items WITHOUT an icon always show their
              label, or they would render as an empty box. */}
          <span className={Icon ? 'hidden lg:inline' : undefined}>{item.label}</span>
          {item.badge !== undefined && (
            <span className="ml-1 rounded-pill bg-white/15 px-1.5 py-0.5 text-xs font-medium text-white">
              {item.badge}
            </span>
          )}
          <span
            aria-hidden="true"
            className={cn(
              navigationPatterns.topBar.linkIndicator,
              isActive
                ? navigationPatterns.topBar.linkIndicatorActive
                : navigationPatterns.topBar.linkIndicatorInactive
            )}
          />
        </>
      )}
    </NavLink>
  );
}
